import { useEffect, useState } from "react";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { ThemeProvider } from "styled-components";
import { StatsigProvider } from "@statsig/react-bindings";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "react-image-gallery/styles/image-gallery.css";

import { Notification } from "@/components/Notification/Notification";
import { SplashScreen } from "@/components";
import { useStatsig } from "@/hooks/useStatsig";
import { useMsalAppReady } from "@/hooks/useMsalAppReady";

import { LocalizationProvider } from "./localization";
import { persistor, store } from "./redux";
import { AppRouter } from "./routes/AppRouter";
import theme from "./theme";

const App = () => {
  return (
    <Provider store={store}>
      <AppShell />
    </Provider>
  );
};

const AppShell = () => {
  const [isRehydrated, setIsRehydrated] = useState(false);
  const [allowProfileFetch, setAllowProfileFetch] = useState(false);
  // Do not send a persisted Redux token to the profile endpoint before MSAL
  // has proved the Entra session is still usable.
  const { client, isReady: isStatsigReady } = useStatsig({
    allowProfileFetch
  });
  const isMsalAppReady = useMsalAppReady(isRehydrated && isStatsigReady);

  useEffect(() => {
    if (!isMsalAppReady) return;
    const timer = globalThis.setTimeout(() => setAllowProfileFetch(true), 0);
    return () => globalThis.clearTimeout(timer);
  }, [isMsalAppReady]);

  useEffect(() => {
    const unsubscribe = persistor.subscribe(() => {
      const state = persistor.getState();
      if (state.bootstrapped) {
        setIsRehydrated(true);
      }
    });

    const currentState = persistor.getState();
    if (currentState.bootstrapped) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsRehydrated(true);
    }

    return unsubscribe;
  }, []);

  return (
    <PersistGate
      loading={<SplashScreen active />}
      persistor={persistor}
    >
      <ThemeProvider theme={theme}>
        <LocalizationProvider>
          <StatsigProvider client={client}>
            <Notification />
            {isRehydrated && isStatsigReady && isMsalAppReady ? (
              <AppRouter />
            ) : (
              <SplashScreen active />
            )}
          </StatsigProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </PersistGate>
  );
};
export default App;
